<template>
    <div class="cost-table-section">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 class="kv-subtitle mb-0">
                {{ t('balance-cost_per_tool') }}
            </h3>
        </div>

        <div v-if="loading" class="py-2">
            <div v-for="i in 3" :key="i" class="d-flex justify-content-between align-items-center py-2" style="border-bottom: 1px solid var(--gray-100, #F1F5F9);">
                <KvSkeleton width="90px" height="14px" />
                <KvSkeleton width="60px" height="14px" />
            </div>
        </div>
        <div v-else-if="costItems.length === 0" class="py-2">
            <div class="text-muted small text-center mb-3">
                {{ t('balance-no_cost_available') }}
            </div>
            <div v-if="user && user.global_admin" class="text-center">
                <router-link :to="'/place/' + place.id + '/plan'" class="btn btn-sm btn-outline-primary w-100">
                    {{ t('balance-add_plan') }}
                </router-link>
            </div>
        </div>
        <template v-else>
            <div ref="listArea" class="cost-list-area">
                <table class="table table-sm cost-table mb-0">
                    <tbody>
                        <tr v-for="item in topItems" :key="item.key">
                            <td class="channel-name">{{ item.label }}</td>
                            <td class="text-right font-weight-500">{{ formatCredits(item.cost) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <button
                v-if="costItems.length > topItems.length"
                type="button"
                class="btn btn-link btn-see-all p-0 mt-2"
                @click="$refs.costDrawer.open()"
            >
                {{ t('balance-see_all_costs').replace('{count}', costItems.length) }}
            </button>
        </template>

        <Drawer ref="costDrawer" id="balance-cost-drawer">
            <template #title>{{ t('balance-cost_per_tool') }}</template>
            <template #body>
                <table class="table table-sm cost-table mb-0">
                    <tbody>
                        <tr v-for="item in costItems" :key="item.key">
                            <td class="channel-name">{{ item.label }}</td>
                            <td class="text-right font-weight-500">{{ formatCredits(item.cost) }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </Drawer>
    </div>
</template>

<script>
import { mapState } from "vuex";
import KvSkeleton from "../../components/ui/kv-skeleton.vue";
import Drawer from "../../components/drawer/drawer.vue";
import FormatMixin from "../mixins/format-mixin.js";

const MIN_SIDEBAR_ITEMS = 3;
const DEFAULT_ROW_HEIGHT = 42;

export default {
    name: "CostTable",
    mixins: [FormatMixin],
    components: {
        KvSkeleton,
        Drawer
    },
    props: {
        creditsCost: { type: Object, default: () => ({}) },
        automationsUsage: { type: Object, default: () => ({ senders: [] }) },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            visibleCount: MIN_SIDEBAR_ITEMS
        };
    },
    computed: {
        ...mapState(['user', 'place']),
        costItems() {
            const channels = Object.entries(this.creditsCost || {})
                .filter(([channel]) => channel)
                .map(([channel, cost]) => ({
                    key: 'channel-' + channel,
                    label: this.$_(channel === 'tasks' ? 'tasks-label' : channel),
                    cost: parseFloat(cost) || 0
                }));

            const senders = (this.automationsUsage.senders || [])
                .filter(sender => sender.alias)
                .map((sender, index) => ({
                    key: 'sender-' + (sender.id || index),
                    label: this.$_(sender.alias),
                    cost: parseFloat(sender.credits_cost) || 0
                }));

            return [...channels, ...senders].sort((a, b) => b.cost - a.cost || a.label.localeCompare(b.label));
        },
        topItems() {
            return this.costItems.slice(0, this.visibleCount);
        }
    },
    mounted() {
        if (typeof ResizeObserver === 'undefined') return;
        this.resizeObserver = new ResizeObserver(() => this.updateVisibleCount());
        this.observeListArea();
    },
    updated() {
        this.observeListArea();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        observeListArea() {
            const area = this.$refs.listArea;
            if (!this.resizeObserver || area === this.observedArea) return;
            this.resizeObserver.disconnect();
            this.observedArea = area;
            if (area) {
                this.resizeObserver.observe(area);
            }
        },
        updateVisibleCount() {
            const area = this.$refs.listArea;
            if (!area) return;
            const row = area.querySelector('tr');
            const rowHeight = (row && row.offsetHeight) || DEFAULT_ROW_HEIGHT;
            const fit = Math.floor((area.clientHeight + 1) / rowHeight);
            const count = Math.max(MIN_SIDEBAR_ITEMS, fit);
            if (count !== this.visibleCount) {
                this.visibleCount = count;
            }
        },
        formatCredits(cost) {
            const unit = cost === 1 ? this.t('balance-credit') : this.t('balance-credits');
            return cost.toLocaleString() + ' ' + unit;
        }
    }
}
</script>

<style scoped lang="scss">
.cost-table-section {
    padding-top: 0;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.cost-list-area {
    flex: 1 1 0;
    min-height: 125px;
    overflow: hidden;
}

.btn-see-all {
    align-self: flex-start;
    font-size: 14px;
    font-weight: 500;
    color: #15803D;
    text-decoration: none;
    box-shadow: none;

    &:hover {
        color: #00542E;
        text-decoration: underline;
    }
}

.kv-subtitle {
    font-size: 14px;
    font-weight: 500;
    color: var(--gray-700, #374151);
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.cost-table {
    border: none;
    
    tr {
        border-bottom: 1px solid var(--gray-100, #F1F5F9);
        
        &:last-child {
            border-bottom: none;
        }
    }
    
    td {
        padding: 10px 0;
        border: none;
        font-size: 14px;
        color: var(--gray-600, #4B5563);
    }
    
    .channel-name {
        text-transform: capitalize;
    }
}

</style>
